/**
 * Display helpers for team-member derived fields.
 *
 * Preference order: persisted DB value first; live formula only when empty.
 * Formulas match handleChangeInput / create payload in CRUDTeamMember.
 */

'use strict';

function calculateYearsAndMonths(givenDate, currentDate) {
  var now = currentDate || new Date();

  var yearsDiff = now.getFullYear() - givenDate.getFullYear();
  var monthsDiff = now.getMonth() - givenDate.getMonth();

  if (now.getDate() < givenDate.getDate()) {
    monthsDiff--;
  }

  if (monthsDiff < 0) {
    yearsDiff--;
    monthsDiff += 12;
  }

  if (Number.isNaN(yearsDiff) || Number.isNaN(monthsDiff)) {
    return '';
  }

  if (yearsDiff < 0 || (yearsDiff === 0 && monthsDiff === 0)) {
    return '0M';
  }

  if (yearsDiff === 0 && monthsDiff > 0) {
    return monthsDiff + 'M';
  }

  return yearsDiff + 'Y ' + monthsDiff + 'M';
}

function calculateNextBussinessDay(startingDateString, daysAfter) {
  var startingDate = new Date(startingDateString);
  var endDate = new Date(startingDate.getTime() + daysAfter * 24 * 60 * 60 * 1000);

  if (endDate.getDay() === 6) {
    endDate.setDate(endDate.getDate() + 2);
  } else if (endDate.getDay() === 0) {
    endDate.setDate(endDate.getDate() + 1);
  }

  var day = endDate.getDate();
  var month = endDate.getMonth() + 1;
  var year = endDate.getFullYear();

  day = day < 10 ? '0' + day : day;
  month = month < 10 ? '0' + month : month;

  if (Number.isNaN(month) && Number.isNaN(day) && Number.isNaN(year)) {
    return '';
  }

  return month + '/' + day + '/' + year;
}

/**
 * Empty means missing, blank, or the dash the UI uses for unset fields.
 */
function hasDerivedValue(value) {
  if (value == null) return false;
  if (typeof value === 'string') {
    var trimmed = value.trim();
    return trimmed !== '' && trimmed !== '-';
  }
  return true;
}

function getAgeDisplay(data, currentDate) {
  if (hasDerivedValue(data && data.AGE)) {
    return data.AGE;
  }
  if (!(data && data.BIRTH_DATE)) {
    return '';
  }
  return calculateYearsAndMonths(new Date(data.BIRTH_DATE), currentDate);
}

function getOnPhonesDisplay(data) {
  if (hasDerivedValue(data && data.ON_PHONES)) {
    return data.ON_PHONES;
  }
  if (!(data && data.STARTING_DATE)) {
    return '';
  }
  return calculateNextBussinessDay(new Date(data.STARTING_DATE), 90);
}

function getInPoolDisplay(data) {
  if (hasDerivedValue(data && data.IN_POOL)) {
    return data.IN_POOL;
  }
  if (!(data && data.STARTING_DATE)) {
    return '';
  }
  return calculateNextBussinessDay(new Date(data.STARTING_DATE), 270);
}

/**
 * Tenure stops at the termination date: someone who left keeps the length of
 * their time at Summit instead of it growing with today's date.
 * Always recalculated — the stored YRS_AT_STRIVE goes stale the day after it
 * is written.
 */
function getYearsAtSummitDisplay(data, currentDate) {
  if (!(data && data.STARTING_DATE)) {
    return '';
  }
  var end = hasDerivedValue(data.TERMINATION_DATE) ? new Date(data.TERMINATION_DATE) : currentDate;
  return calculateYearsAndMonths(new Date(data.STARTING_DATE), end);
}

/**
 * Normalize DB ISO dates and live MM/DD/YYYY display strings to a timestamp
 * so list sorts match what the In Pool / On Phones columns show.
 */
function toSortableDateValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (value instanceof Date) {
    var dateMs = value.getTime();
    return Number.isNaN(dateMs) ? null : dateMs;
  }
  var str = String(value).trim();
  if (!str || str === '-') return null;
  var mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(str);
  if (mdy) {
    var parsed = new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2])).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  var fromNative = new Date(str).getTime();
  return Number.isNaN(fromNative) ? null : fromNative;
}

/**
 * Team Members list only shows On Phones / In Pools for the current year or later.
 * Profile still shows the full date via getOnPhonesDisplay / getInPoolDisplay.
 */
function isCurrentYearOrFuture(value, currentDate) {
  var ms = toSortableDateValue(value);
  if (ms == null) return false;
  var now = currentDate || new Date();
  return new Date(ms).getFullYear() >= now.getFullYear();
}

/**
 * On Phones / In Pools only apply to Summit Agents (and legacy agent code) —
 * the list column and its sort must agree on this, or a row with no visible
 * date still lands in date order via a value nobody can see.
 */
function appliesToPhonesAndPools(roleCode) {
  return roleCode === 'a' || roleCode === 'y';
}

/**
 * List display for On Phones / In Pools: same derivation as the profile, but
 * blank when the date is before the current calendar year.
 */
function getOnPhonesListDisplay(data, currentDate) {
  var value = getOnPhonesDisplay(data);
  return isCurrentYearOrFuture(value, currentDate) ? value : '';
}

function getInPoolListDisplay(data, currentDate) {
  var value = getInPoolDisplay(data);
  return isCurrentYearOrFuture(value, currentDate) ? value : '';
}

/**
 * Sort accessor for Team Members list columns whose display value may be
 * derived (In Pool / On Phones fall back to Start Date + N business days;
 * Years At Summit is always live-calculated from Start Date).
 *
 * IN_POOL/ON_PHONES sort by the exact value the column renders (role-gated,
 * past-year hidden) — a row whose cell is blank must sort as empty too,
 * not by a hidden real date.
 */
function getTeamMemberSortValue(item, code) {
  if (code === 'IN_POOL') {
    if (!appliesToPhonesAndPools(item && item.IS_AGENT)) return null;
    return toSortableDateValue(getInPoolListDisplay(item));
  }
  if (code === 'ON_PHONES') {
    if (!appliesToPhonesAndPools(item && item.IS_AGENT)) return null;
    return toSortableDateValue(getOnPhonesListDisplay(item));
  }
  if (code === 'YRS_AT_STRIVE') {
    // List renders getYearsAtSummitDisplay, not YRS_AT_STRIVE. Sort by the
    // length itself — for a terminated member that is no longer a function of
    // the start date alone.
    return getTenureMs(item);
  }
  return item ? item[code] : null;
}

/** Length of getYearsAtSummitDisplay, in milliseconds, for sorting. */
function getTenureMs(data, currentDate) {
  var startMs = toSortableDateValue(data && data.STARTING_DATE);
  if (startMs == null) return null;
  var endMs = hasDerivedValue(data.TERMINATION_DATE)
    ? toSortableDateValue(data.TERMINATION_DATE)
    : (currentDate || new Date()).getTime();
  return endMs == null ? null : endMs - startMs;
}

module.exports = {
  hasDerivedValue: hasDerivedValue,
  getAgeDisplay: getAgeDisplay,
  getOnPhonesDisplay: getOnPhonesDisplay,
  getInPoolDisplay: getInPoolDisplay,
  getYearsAtSummitDisplay: getYearsAtSummitDisplay,
  getOnPhonesListDisplay: getOnPhonesListDisplay,
  getInPoolListDisplay: getInPoolListDisplay,
  isCurrentYearOrFuture: isCurrentYearOrFuture,
  appliesToPhonesAndPools: appliesToPhonesAndPools,
  toSortableDateValue: toSortableDateValue,
  getTeamMemberSortValue: getTeamMemberSortValue,
  calculateYearsAndMonths: calculateYearsAndMonths,
  calculateNextBussinessDay: calculateNextBussinessDay,
};
